import { MarketRow, MarketToggle } from '@market/react';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  EmailPreference,
  Product as EmailPreferenceProduct,
  MerchantEmailPreference,
  Value,
} from 'routes/profile/models/EmailPreference';
import MerchantReceiptPrefs from './MerchantReceiptPrefs';
import { useUpdateEmailPreferenceMutation } from 'store/query/api-extensions/receiptAndMarketingPreferences';
import { openToast, ToastVariant } from 'store/toastSlice';
import BuyerCard from 'routes/profile/models/BuyerCard';
import { useTranslation } from 'react-i18next';
import { buyerportalCdpClient } from 'services/tracking/cdp/clients/buyerportal';
import { actionToggleAutoReceipts } from 'services/tracking/cdp/events/receiptSettings';
import { isPreconditionFailedError } from 'store/query/utils';

type ReceiptPreferenceTogglesProps = {
  deliveryEmailId: string | null;
  selectedPaymentCard: BuyerCard;
  isGloballyOptedIn: boolean;
  isGlobalToggleDisabled: boolean;
  merchantPrefs: MerchantEmailPreference[];
  isMerchantEmailPreferencesLoading: boolean;
};

const ReceiptPreferenceToggles: React.FC<ReceiptPreferenceTogglesProps> = ({
  deliveryEmailId,
  selectedPaymentCard,
  merchantPrefs,
  isGloballyOptedIn,
  isGlobalToggleDisabled,
  isMerchantEmailPreferencesLoading,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [updateEmailPreference, updateEmailPreferenceResult] =
    useUpdateEmailPreferenceMutation();

  const toggleGlobalReceiptPreference = (
    isToggledOn: boolean,
    prefCardId: string
  ) => {
    updateEmailPreference({
      cardId: prefCardId,
      emailId: deliveryEmailId as string,
      preference: new EmailPreference({
        product: EmailPreferenceProduct.Receipt,
        value: isToggledOn ? Value.OptIn : Value.OptOut,
      }),
    })
      .unwrap()
      .then(() => {
        buyerportalCdpClient.track(
          'Toggle Auto Receipts',
          actionToggleAutoReceipts(isToggledOn)
        );

        dispatch(
          openToast({
            message: t('common.changeSuccess'),
            variant: ToastVariant.Success,
          })
        );
      })
      .catch((ex) => {
        // When the email linked to the card isn't a verified, selectable
        // identifier, the backend rejects the receipt opt-in with
        // PRECONDITION_FAILED. Surface an actionable message telling the buyer
        // to verify it from their profile instead of the generic error.
        dispatch(
          openToast({
            message: isPreconditionFailedError(ex)
              ? t(
                  'profile.account.contactInfo.addContactInfoModal.request.emailAlreadyExists'
                )
              : t('common.somethingWentWrong.retryable.direct'),
            variant: ToastVariant.Critical,
          })
        );
      });
  };

  // No email linked to card => enabling receipts doesn't make sense because there's no email to send them to
  // If still global update pending, disable the toggle
  const shouldDisableGlobalToggle =
    isGlobalToggleDisabled ||
    !deliveryEmailId ||
    updateEmailPreferenceResult.isLoading;

  const renderMerchantPrefs = () => {
    if (!isGloballyOptedIn) {
      return null;
    }
    return (
      <MerchantReceiptPrefs
        emailId={deliveryEmailId as string}
        cardId={selectedPaymentCard.preferenceCardId}
        merchantPrefs={merchantPrefs}
        isMerchantPrefsLoading={
          isMerchantEmailPreferencesLoading ||
          updateEmailPreferenceResult.isLoading
        }
      />
    );
  };

  return (
    <>
      <h2 className={'heading-20 mt-8 mb-0'}>
        {t('profile.receiptSettings.receiptPreferenceToggles.title')}
      </h2>
      <div className="my-4">
        <p>{t('profile.receiptSettings.receiptPreferenceToggles.subtitle')}</p>
      </div>
      <MarketRow
        id="receipt-pref-auto-receipt-row"
        onMarketRowDeselected={() =>
          toggleGlobalReceiptPreference(
            false,
            selectedPaymentCard.preferenceCardId
          )
        }
        onMarketRowSelected={() =>
          toggleGlobalReceiptPreference(
            true,
            selectedPaymentCard.preferenceCardId
          )
        }
        selected={isGloballyOptedIn}
        disabled={shouldDisableGlobalToggle}
      >
        <div id="global-receipts-label">
          <label className="medium-20 my-0 text-gray-500">
            {t(
              'profile.receiptSettings.receiptPreferenceToggles.receiveAutoEmails'
            )}
          </label>
          <p className="my-0 text-gray-500">
            {t(
              'profile.receiptSettings.receiptPreferenceToggles.receiptDefaultEmailAddress'
            )}
          </p>
        </div>
        <MarketToggle
          slot="control"
          role="switch"
          aria-checked={isGloballyOptedIn}
          aria-labelledby="global-receipts-label"
        />
      </MarketRow>

      {renderMerchantPrefs()}
    </>
  );
};

export default ReceiptPreferenceToggles;
